import Alert from '@mui/material/Alert';
import CardHeader from '@mui/material/CardHeader';
import Chat from 'components/shared/Chat';
import Close from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import PropTypes from 'prop-types';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { UserAvatar, UserName } from '../User';
import { useTwilio } from 'providers/TwilioProvider/hooks/public/useTwilio';

/** TODO: Collapse UserMessagingDialog and EstheticianMessagingDialog into one.
 * It's more flexible to use the notion of incoming / outgoing messages instead
 * of whether they're from estheticians / clients, especially when we introduce
 * Pomp Concierge.
 */
export const UserMessagingDialog = ({
  userId,
  estheticianId,
  isEsthetician,
  cancel,
}) => {
  const { conversations } = useTwilio();
  const headerRef = useRef(null);
  const alertRef = useRef(null);

  const [shouldDisplaySMSAlert, setShouldDisplaySMSAlert] = useState(false);

  useLayoutEffect(() => {
    setShouldDisplaySMSAlert(true);
  }, []);

  const conversation = useMemo(() => {
    if (!conversations || !userId) return null;

    return (
      Object.values(conversations).find(
        ({ uniqueName }) =>
          uniqueName.startsWith(`conversation:${userId}`) &&
          (isEsthetician || uniqueName.endsWith(`-${estheticianId}`))
      ) ?? null
    );
  }, [conversations, userId, estheticianId, isEsthetician]);

  return (
    <>
      <CardHeader
        ref={headerRef}
        sx={{ backgroundColor: 'primary.main' }}
        avatar={<UserAvatar userId={isEsthetician ? userId : estheticianId} />}
        title={<UserName userId={isEsthetician ? userId : estheticianId} />}
        titleTypographyProps={{
          variant: 'h6',
          color: 'primary.contrastText',
          fontWeight: 400,
        }}
        action={
          <IconButton onClick={cancel}>
            <Close sx={{ color: 'primary.contrastText' }} />
          </IconButton>
        }
      />

      <If condition={shouldDisplaySMSAlert}>
        <Alert
          ref={alertRef}
          sx={{
            position: 'fixed',
            top: `${headerRef?.current?.clientHeight}px`,
            zIndex: 10000,
            width: '100%',
            maxWidth: '698px',
            borderRadius: '0px',
          }}
          severity="info"
          icon={false}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setShouldDisplaySMSAlert(false)}
            >
              <Close fontSize="inherit" />
            </IconButton>
          }
        >
          Messages sent from here will also be delivered via text to your phone
          to ensure complete context.
        </Alert>
      </If>

      <Chat
        selectedConversation={conversation}
        isRenderedWithinDrawer={true}
        userId={userId}
        estheticianId={estheticianId}
      />
    </>
  );
};

UserMessagingDialog.propTypes = {
  userId: PropTypes.string.isRequired,
  cancel: PropTypes.func,
  estheticianId: PropTypes.string,
  isEsthetician: PropTypes.bool.isRequired,
};
